
import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Bell, Mail, MessageSquare, Calendar, Star, AlertCircle, Save } from "lucide-react";
import { useNotificationPreferences } from "@/hooks/useNotificationPreferences";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useToast } from "@/hooks/use-toast";

const VendorNotifications = () => {
  const { id: serviceId } = useParams();
  const { preferences, loading, saving, savePreferences } = useNotificationPreferences();
  const { toast } = useToast();

  // Local form state
  const [formData, setFormData] = useState({
    notification_email: '',
    notification_phone: '',
    email_new_bookings: true,
    email_booking_updates: true,
    email_reviews: true,
    sms_new_bookings: true,
    sms_booking_updates: true,
    sms_reviews: true,
  });

  // Initialize form data when preferences are loaded
  useEffect(() => {
    if (preferences) {
      setFormData({
        notification_email: preferences.notification_email || '',
        notification_phone: preferences.notification_phone || '',
        email_new_bookings: preferences.email_new_bookings,
        email_booking_updates: preferences.email_booking_updates,
        email_reviews: preferences.email_reviews,
        sms_new_bookings: preferences.sms_new_bookings,
        sms_booking_updates: preferences.sms_booking_updates,
        sms_reviews: preferences.sms_reviews,
      });
    }
  }, [preferences]);

  if (loading) {
    return (
      <div className="space-y-6 p-6">
        <div className="space-y-2">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-4 w-96" />
        </div>
        <Skeleton className="h-[400px] w-full" />
      </div>
    );
  }

  if (!preferences) {
    return (
      <div className="p-6">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Failed to load notification preferences. Please try refreshing the page.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  const handleContactInfoChange = (field: 'notification_email' | 'notification_phone', value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleNotificationToggle = (field: keyof typeof formData, value: boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    try {
      await savePreferences(formData);
      toast({
        title: "Settings saved",
        description: "Your notification preferences have been updated successfully.",
      });
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to save notification preferences. Please try again.",
      });
    }
  };

  // Check if form has changes
  const hasChanges = preferences && (
    formData.notification_email !== (preferences.notification_email || '') ||
    formData.notification_phone !== (preferences.notification_phone || '') ||
    formData.email_new_bookings !== preferences.email_new_bookings ||
    formData.email_booking_updates !== preferences.email_booking_updates ||
    formData.email_reviews !== preferences.email_reviews ||
    formData.sms_new_bookings !== preferences.sms_new_bookings ||
    formData.sms_booking_updates !== preferences.sms_booking_updates ||
    formData.sms_reviews !== preferences.sms_reviews
  );

  return (
    <div className="space-y-6 p-6">
      <div className="space-y-2">
        <h1 className="text-2xl font-semibold flex items-center gap-2">
          <Bell className="h-6 w-6" />
          Notification Settings
        </h1>
        <p className="text-muted-foreground">
          Manage how and where you receive notifications about your service bookings and reviews.
        </p>
      </div>

      <div className="grid gap-6">
        {/* Contact Information */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Mail className="h-5 w-5" />
              Contact Information
            </CardTitle>
            <CardDescription>
              Specify the email address and phone number for notifications. Your profile information is used as default.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="notification_email">Email Address</Label>
              <Input
                id="notification_email"
                type="email"
                value={formData.notification_email}
                onChange={(e) => handleContactInfoChange('notification_email', e.target.value)}
                placeholder="Enter email for notifications"
                disabled={saving}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="notification_phone">Phone Number</Label>
              <Input
                id="notification_phone"
                type="tel"
                value={formData.notification_phone}
                onChange={(e) => handleContactInfoChange('notification_phone', e.target.value)}
                placeholder="Enter phone number for SMS notifications (e.g., 254700000000)"
                disabled={saving}
              />
              <p className="text-sm text-muted-foreground">
                Include country code for international numbers
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Email Notifications */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Mail className="h-5 w-5" />
              Email Notifications
            </CardTitle>
            <CardDescription>
              Choose which email notifications you want to receive
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <Label htmlFor="email_new_bookings">New Bookings</Label>
                </div>
                <p className="text-sm text-muted-foreground">
                  Get notified when customers book your service
                </p>
              </div>
              <Switch
                id="email_new_bookings"
                checked={formData.email_new_bookings}
                onCheckedChange={(checked) => handleNotificationToggle('email_new_bookings', checked)}
                disabled={saving}
              />
            </div>

            <Separator />

            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <Label htmlFor="email_booking_updates">Booking Updates</Label>
                </div>
                <p className="text-sm text-muted-foreground">
                  Get notified when booking status changes
                </p>
              </div>
              <Switch
                id="email_booking_updates"
                checked={formData.email_booking_updates}
                onCheckedChange={(checked) => handleNotificationToggle('email_booking_updates', checked)}
                disabled={saving}
              />
            </div>

            <Separator />

            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <Star className="h-4 w-4 text-muted-foreground" />
                  <Label htmlFor="email_reviews">New Reviews</Label>
                </div>
                <p className="text-sm text-muted-foreground">
                  Get notified when customers leave reviews
                </p>
              </div>
              <Switch
                id="email_reviews"
                checked={formData.email_reviews}
                onCheckedChange={(checked) => handleNotificationToggle('email_reviews', checked)}
                disabled={saving}
              />
            </div>
          </CardContent>
        </Card>

        {/* SMS Notifications */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <MessageSquare className="h-5 w-5" />
              SMS Notifications
            </CardTitle>
            <CardDescription>
              Manage your SMS notification preferences
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <Label htmlFor="sms_new_bookings">New Bookings</Label>
                </div>
                <p className="text-sm text-muted-foreground">
                  Get SMS alerts when customers book your service
                </p>
              </div>
              <Switch
                id="sms_new_bookings"
                checked={formData.sms_new_bookings}
                onCheckedChange={(checked) => handleNotificationToggle('sms_new_bookings', checked)}
                disabled={saving}
              />
            </div>

            <Separator />

            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <Label htmlFor="sms_booking_updates">Booking Updates</Label>
                </div>
                <p className="text-sm text-muted-foreground">
                  Get SMS alerts when booking status changes
                </p>
              </div>
              <Switch
                id="sms_booking_updates"
                checked={formData.sms_booking_updates}
                onCheckedChange={(checked) => handleNotificationToggle('sms_booking_updates', checked)}
                disabled={saving}
              />
            </div>

            <Separator />

            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <Star className="h-4 w-4 text-muted-foreground" />
                  <Label htmlFor="sms_reviews">New Reviews</Label>
                </div>
                <p className="text-sm text-muted-foreground">
                  Get SMS alerts when customers leave reviews
                </p>
              </div>
              <Switch
                id="sms_reviews"
                checked={formData.sms_reviews}
                onCheckedChange={(checked) => handleNotificationToggle('sms_reviews', checked)}
                disabled={saving}
              />
            </div>
          </CardContent>
        </Card>

        {/* Submit Button */}
        <div className="flex justify-end">
          <Button 
            onClick={handleSubmit}
            disabled={saving || !hasChanges}
            className="flex items-center gap-2"
          >
            <Save className="h-4 w-4" />
            {saving ? "Saving..." : "Save Settings"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default VendorNotifications;
